import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { recurringJobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import type { Job } from '@/types/job';

/**
 * NOT-339 §8 — recurring series lifecycle on the parent Job.
 * Endpoints: POST /v2/jobs/:id/{activate,pause,resume,cancel,renew}.
 * Distinct from the one-off pipeline cancel (useCancelJob).
 */

export type PauseBillingPolicy = 'CONTINUE' | 'PRORATE_CREDIT' | 'SUSPEND';
export type CancellationPath =
  | 'EXPIRE'
  | 'CUSTOMER_CANCEL'
  | 'NON_PAYMENT'
  | 'MIGRATE_TO_NEW'
  | 'BANKRUPT_CUSTOMER';

export interface PauseJobInput {
  pauseStart: string;
  pauseEnd: string;
  billingPolicy?: PauseBillingPolicy;
}

export interface CancelRecurringJobInput {
  path: CancellationPath;
  reason?: string;
  effectiveDate?: string;
  cancelReasonCategory?: string;
  saveOfferType?: string;
  cancellationMode?: 'IMMEDIATE' | 'AT_PERIOD_END';
}

function useInvalidateJob(jobId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    void qc.invalidateQueries({ queryKey: jobKeys.appointments(jobId) });
    void qc.invalidateQueries({ queryKey: jobKeys.lists() });
  };
}

export function useActivateJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<Job, void>({
    mutationFn: () => apiPost<Job>(operationsClient, recurringJobEndpoints.activate(jobId)),
    onSuccess: () => {
      invalidate();
      toast.success('Recurring plan activated');
    },
  });
}

export function usePauseJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<Job, PauseJobInput>({
    mutationFn: (body) => apiPost<Job>(operationsClient, recurringJobEndpoints.pause(jobId), body),
    onSuccess: () => {
      invalidate();
      toast.success('Recurring plan paused');
    },
  });
}

export function useResumeJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<Job, void>({
    mutationFn: () => apiPost<Job>(operationsClient, recurringJobEndpoints.resume(jobId)),
    onSuccess: () => {
      invalidate();
      toast.success('Recurring plan resumed');
    },
  });
}

export function useCancelRecurringJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<Job, CancelRecurringJobInput>({
    mutationFn: (body) => apiPost<Job>(operationsClient, recurringJobEndpoints.cancel(jobId), body),
    onSuccess: () => {
      invalidate();
      toast.success('Recurring plan cancelled');
    },
  });
}

/** Keep a series whose cancellation was set for a later date. */
export function useKeepRecurringJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<unknown, void>({
    mutationFn: () => apiPost(operationsClient, recurringJobEndpoints.withdrawCancellation(jobId)),
    onSuccess: () => {
      invalidate();
      toast.success('Series kept — it will not end');
    },
  });
}

export function useRenewJob(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useApiMutation<Job, void>({
    mutationFn: () => apiPost<Job>(operationsClient, recurringJobEndpoints.renew(jobId)),
    onSuccess: () => {
      invalidate();
      toast.success('Recurring plan renewed');
    },
  });
}
